import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import BottomNavigation from '@/components/ui/BottomNavigation';
import { cn } from '@/utils/cn';
import { toPersianNumber } from '@/utils/persianNumber';

import { validatePhone, validateUserId, formatPhoneNumber } from '@/utils/validation';
import { logoutUser, getMe, updateMe } from '@/services/authService';
import type { UserProfile } from '@/services/authService';
import SelectionCard from '@/components/ui/SelectionCard';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import { useUserTopics } from '@/features/learning/hooks/useUserTopics';
import { addUserTopics, removeUserTopics } from '@/features/learning/services/topicService';
import SupportTicket from '../components/SupportTicket';
import { analyticsService } from '@/services/analyticsService';
import { User, Phone, AtSign, BookOpen, Headphones, History, FileText, LogOut, ChevronLeft } from 'lucide-react';

interface SettingsPageProps {
  className?: string;
}

const SettingsPage = ({ className }: SettingsPageProps) => {
  const { logout, updateUser } = useAuth();
  const navigate = useNavigate();
  // Toast system removed

  // State management
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingUserId, setIsEditingUserId] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [userIdValue, setUserIdValue] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [userIdSuccess, setUserIdSuccess] = useState('');
  const [customUserId, setCustomUserId] = useState(''); // Store user's custom ID
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [tempSelectedTopicIds, setTempSelectedTopicIds] = useState<number[]>([]);
  const [topicsSaveSuccess, setTopicsSaveSuccess] = useState('');
  const [topicsSaveError, setTopicsSaveError] = useState('');
  const [isSavingTopics, setIsSavingTopics] = useState(false);
  const [showSupportTicket, setShowSupportTicket] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // User topics hook
  const { userTopics, loading: topicsLoading, error: topicsError, refetch: refetchTopics } = useUserTopics();
  // Handle back navigation
  const handleBack = () => {
    navigate('/profile');
  };

  // Load live profile from backend for display (read-only)
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const me = await getMe();
        setProfile(me);
      } catch (error) {
        console.error('[Settings] Failed to load profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, []);

  // Handle name editing
  const handleNameClick = () => {
    setIsEditingName(true);
    setNameValue(profile?.name ?? '');
    setNameError('');
    setNameSuccess('');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleNameBlur = async () => {
    const trimmedName = nameValue.trim();
    try {
      if (trimmedName && trimmedName !== (profile?.name ?? '')) {
        console.log('[Settings] Sending updateMe payload (name):', { name: trimmedName });
        const updated = await updateMe({ name: trimmedName });
        setProfile(updated);
        updateUser({ name: updated.name ?? '' });
        setNameSuccess('نام با موفقیت به‌روزرسانی شد');
        setNameError('');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'خطا در به‌روزرسانی نام';
      setNameError(msg);
      setNameSuccess('');
    } finally {
      setIsEditingName(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    }
    if (e.key === 'Escape') {
      setNameValue(profile?.name ?? '');
      setIsEditingName(false);
    }
  };

  // Handle user ID editing
  const handleUserIdClick = () => {
    setIsEditingUserId(true);
    // Initialize from live profile username (without @)
    const currentId = (profile?.username ?? '').replace('@', '');
    setUserIdValue(currentId);
    setUserIdError('');
    setUserIdSuccess('');
  };

  const handleUserIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUserIdValue(newValue);

    // Clear error when user starts typing
    if (userIdError) {
      setUserIdError('');
    }
  };

  const handleUserIdBlur = async () => {
    const validation = validateUserId(userIdValue);

    if (!validation.isValid && userIdValue.trim()) {
      setUserIdError(validation.error || 'شناسه کاربری نامعتبر است');
      return;
    }

    const trimmedId = userIdValue.trim();
    try {
      if (trimmedId && trimmedId !== (profile?.username ?? '')) {
        console.log('[Settings] Sending updateMe payload (username):', { username: trimmedId });
        const updated = await updateMe({ username: trimmedId });
        setProfile(updated);
        // Clear any custom local id usage and show success
        setCustomUserId('');
        setUserIdSuccess('شناسه کاربری با موفقیت به‌روزرسانی شد');
        setUserIdError('');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'خطا در به‌روزرسانی شناسه کاربری';
      setUserIdError(msg);
      setUserIdSuccess('');
    } finally {
      setIsEditingUserId(false);
    }
  };

  const handleUserIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUserIdBlur();
    }
    if (e.key === 'Escape') {
      const currentId = (profile?.username ?? '').replace('@', '');
      setUserIdValue(currentId);
      setIsEditingUserId(false);
      setUserIdError('');
      setUserIdSuccess('');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    // First click: open confirm prompt UI
    if (!showLogoutConfirm) {
      setShowLogoutConfirm(true);
      return;
    }

    // Second click: user confirmed -> call backend, then clear tokens and redirect
    try {
      const message = await logoutUser();
      void analyticsService.logEvent('logout_success');
      // Clear auth and navigate right away; no toast per user request
      logout();
      setShowLogoutConfirm(false);
      if ((window as any).recheckAuth) {
        try { await (window as any).recheckAuth(); } catch { }
      }
      navigate('/welcome');
    } catch (e) {
      // Silently fail per user request (no toast/alert)
    }
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Handle premium purchase
  const handlePremiumPurchaseClick = () => {
    navigate('/premium-subscription');
  };

  const handlePaymentHistoryClick = () => {
    navigate('/payment-history');
  };

  const handleReportHistoryClick = () => {
    navigate('/report-history');
  };


  // Handle topics modal
  const isTopicSelected = (topic: { state?: unknown }) => topic?.state === true || topic?.state === 'selected';
  const handleTopicsClick = () => {
    // Initialize selected topics from backend state
    if (!Array.isArray(userTopics)) {
      setSelectedTopicIds([]);
      setTempSelectedTopicIds([]);
      setShowTopicsModal(true);
      return;
    }
    const initialSelected = userTopics
      .filter(t => isTopicSelected(t))
      .map(topic => topic.topic_id);
    setSelectedTopicIds(initialSelected);
    setTempSelectedTopicIds([...initialSelected]);
    setShowTopicsModal(true);
  };

  const handleTopicToggle = (topicId: number) => {
    setTempSelectedTopicIds(prev => {
      const isSelected = prev.includes(topicId);
      return isSelected
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId];
    });
  };

  const handleTopicsSave = async () => {
    try {
      setIsSavingTopics(true);
      setTopicsSaveError('');
      setTopicsSaveSuccess('');

      // Get currently selected topics from backend state
      const currentlySelected = Array.isArray(userTopics)
        ? userTopics
          .filter(t => isTopicSelected(t))
          .map(topic => topic.topic_id)
        : [];

      // Calculate differences
      const toAdd = tempSelectedTopicIds.filter(id => !currentlySelected.includes(id));
      const toRemove = currentlySelected.filter(id => !tempSelectedTopicIds.includes(id));

      console.log('Topics diff:', { currentlySelected, tempSelectedTopicIds, toAdd, toRemove });

      // Only make API calls if there are changes
      let hasChanges = false;

      // Add new topics
      if (toAdd.length > 0) {
        console.log('Adding topics:', toAdd);
        const addResult = await addUserTopics(toAdd);
        if (!addResult.success) {
          throw new Error(addResult.message || 'Failed to add topics');
        }
        hasChanges = true;
      }

      // Remove unselected topics
      if (toRemove.length > 0) {
        console.log('Removing topics:', toRemove);
        const removeResult = await removeUserTopics(toRemove);
        if (!removeResult.success) {
          throw new Error(removeResult.message || 'Failed to remove topics');
        }
        hasChanges = true;
      }

      if (hasChanges) {
        // Refresh topics data from backend
        await refetchTopics();
        console.log('✅ Topics updated successfully');
        setTopicsSaveSuccess('تغییرات با موفقیت انجام شد');
      }

      // Update local state and close modal
      setSelectedTopicIds([...tempSelectedTopicIds]);

      // Close modal after a short delay to show success message
      setTimeout(() => {
        setShowTopicsModal(false);
        setTopicsSaveSuccess('');
      }, 1500);

    } catch (error) {
      console.error('❌ Error saving topics:', error);
      const errorMessage = error instanceof Error ? error.message : 'خطا در ذخیره موضوعات';
      setTopicsSaveError(errorMessage);
    } finally {
      setIsSavingTopics(false);
    }
  };

  const handleTopicsCancel = () => {
    setTempSelectedTopicIds([...selectedTopicIds]);
    setShowTopicsModal(false);
  };

  const handleSupportClick = () => {
    setShowSupportTicket(true);
  };

  const handleCloseSupportTicket = () => {
    setShowSupportTicket(false);
  };

  // Removed unused getUserId to satisfy lint

  // Get display value for contact info




  // Get display text for selected topics
  const getTopicsDisplayText = () => {
    if (!Array.isArray(userTopics)) {
      return 'هیچ موضوعی انتخاب نشده';
    }
    const isSelected = (s: unknown) => s === true || s === 'selected';
    const selectedCount = userTopics.filter(topic => isSelected(topic.state)).length;
    if (selectedCount === 0) {
      return 'هیچ موضوعی انتخاب نشده';
    } else if (selectedCount === 1) {
      const selectedTopic = userTopics.find(topic => isSelected(topic.state));
      return selectedTopic?.topic_name || 'موضوع انتخاب شده';
    } else {
      return `${selectedCount} موضوع انتخاب شده`;
    }
  };

  return (
    <div className={cn('min-h-screen bg-background', className)} dir="rtl">
      {/* Header */}
      <div className="bg-[#ffffff] px-4 py-3 flex items-center justify-between sticky top-0 z-50 pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button
          onClick={handleBack}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        <h1 className="text-sm md:text-base font-bold text-foreground">تنظیمات</h1>
        <div className="w-8"></div>
        </div>
      </div>

      <div
        className="pb-20 mt-6"
      >
        {/* Group 1: Personal Info */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100 mx-4 mb-4">
          {/* Name */}
          <div
            onClick={!isEditingName && !profileLoading ? handleNameClick : undefined}
            className={cn(
              "w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors group",
              !isEditingName && !profileLoading && "cursor-pointer"
            )}
          >
            <div className="flex items-center gap-4 flex-1">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500 group-hover:scale-110 transition-transform flex-shrink-0">
                <User className="w-5 h-5" />
              </div>
              <div className="text-right flex-1">
                <div className="font-bold text-gray-900 font-persian text-sm mb-1">نام</div>
                {profileLoading ? (
                  <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" />
                ) : !isEditingName ? (
                  <div className="text-xs text-muted font-persian hover:text-primary-600 transition-colors text-right w-full">
                    {profile?.name ? toPersianNumber(profile.name) : 'نام ثبت نشده'}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={nameValue}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    onKeyDown={handleNameKeyDown}
                    autoFocus
                    className="w-full text-xs text-foreground bg-transparent font-persian border-b border-primary-300 outline-none focus:border-primary-500 py-0.5"
                    placeholder="نام خود را وارد کنید"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {nameError && <p className="text-[10px] text-red-500 mt-1">{nameError}</p>}
                {nameSuccess && <p className="text-[10px] text-green-600 mt-1">{nameSuccess}</p>}
              </div>
            </div>
          </div>
          <div className="h-px bg-gray-50 mx-4" />

          {/* Phone - Read Only */}
          <div className="w-full flex items-center justify-between p-4 rounded-2xl">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-500 flex-shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div className="text-right flex-1">
                <div className="font-bold text-gray-900 font-persian text-sm mb-1">شماره همراه</div>
                {profileLoading ? (
                  <div className="h-4 w-28 rounded bg-gray-200 animate-pulse" />
                ) : (
                  <div className="text-xs text-muted font-persian text-right w-full">
                    {(() => {
                      const phone = profile?.phone_number ?? '';
                      return phone ? toPersianNumber(formatPhoneNumber(phone)) : 'شماره همراه ثبت نشده';
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="h-px bg-gray-50 mx-4" />

          {/* User ID */}
          <div
            onClick={!isEditingUserId ? handleUserIdClick : undefined}
            className={cn(
              "w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors group",
              !isEditingUserId && "cursor-pointer"
            )}
          >
            <div className="flex items-center gap-4 flex-1">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500 group-hover:scale-110 transition-transform flex-shrink-0">
                <AtSign className="w-5 h-5" />
              </div>
              <div className="text-right flex-1">
                <div className="font-bold text-gray-900 font-persian text-sm mb-1">شناسه کاربری</div>
                {profileLoading ? (
                  <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
                ) : !isEditingUserId ? (
                  <div className="text-xs text-muted font-persian hover:text-primary-600 transition-colors text-right w-full">
                    {profile?.username ? `${toPersianNumber(profile.username)}@` : 'شناسه کاربری ثبت نشده'}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={userIdValue}
                    onChange={handleUserIdChange}
                    onBlur={handleUserIdBlur}
                    onKeyDown={handleUserIdKeyDown}
                    autoFocus
                    className="w-full text-xs text-foreground bg-transparent font-persian border-b border-primary-300 outline-none focus:border-primary-500 py-0.5"
                    placeholder="شناسه کاربری@"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {userIdError && <p className="text-[10px] text-red-500 mt-1">{userIdError}</p>}
                {userIdSuccess && <p className="text-[10px] text-green-600 mt-1">{userIdSuccess}</p>}
              </div>
            </div>
          </div>
        </div>



        {/* Group 3: History & Support */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100 mx-4 mb-4">
          {/* Payment History */}
          <button
            type="button"
            onClick={handlePaymentHistoryClick}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-500 group-hover:scale-110 transition-transform">
                <History className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 font-persian text-sm">تاریخچه پرداخت</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div className="h-px bg-gray-50 mx-4" />

          {/* Report History */}
          <button
            type="button"
            onClick={handleReportHistoryClick}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-500 group-hover:scale-110 transition-transform">
                <FileText className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 font-persian text-sm">تاریخچه گزارش‌ها</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div className="h-px bg-gray-50 mx-4" />

          {/* Support */}
          <button
            type="button"
            onClick={handleSupportClick}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 group-hover:scale-110 transition-transform">
                <Headphones className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 font-persian text-sm">پشتیبانی</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-gray-300" />
          </button>
        </div>

        {/* Group 4: Actions */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100 mx-4 mb-4">
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between p-4 hover:bg-red-50 rounded-2xl transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform">
                <LogOut className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="font-bold text-red-600 font-persian text-sm">خروج از حساب</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-red-200" />
          </button>
        </div>

        {/* App Info Section */}
        <div className="px-6 mb-8">
          <p className="text-xs text-muted text-center font-persian">
            نسخه ۱.۰.۰
          </p>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <div
        aria-hidden={!showLogoutConfirm}
        className={cn(
          'transition-opacity duration-200 ease-out',
          showLogoutConfirm ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-40 z-50"
            />

            {/* Confirmation Modal */}
            <div
              className={cn(
                'fixed inset-0 z-50 flex items-center justify-center p-4',
                'transition-[transform,opacity] duration-200 ease-out',
                showLogoutConfirm
                  ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
                  : 'pointer-events-none translate-y-3 scale-95 opacity-0'
              )}
            >
              <div
                className={cn(
                  'bg-soft-white rounded-card p-6',
                  'border border-soft-neutral-200 shadow-xl',
                  'max-w-sm w-full mx-4'
                )}
                dir="rtl"
              >
                <p className={cn(
                  'text-base text-foreground text-center mb-8',
                  'font-persian'
                )}>
                  آیا مطمئن هستید که می‌خواهید خارج شوید؟
                </p>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={cancelLogout}
                    className={cn(
                      'flex-1 py-3 text-center',
                      'text-muted font-medium font-persian',
                      'hover:text-foreground transition-colors'
                    )}
                  >
                    انصراف
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={cn(
                      'flex-1 py-3 text-center',
                      'text-destructive font-medium font-persian',
                      'hover:text-red-700 transition-colors'
                    )}
                  >
                    خروج
                  </button>
                </div>
              </div>
            </div>
      </div>

      {/* Topics Modal */}
      {showTopicsModal && (
        <div>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 z-[100]"
          onClick={handleTopicsCancel}
        />

        {/* Modal Content */}
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className={cn(
              'w-full max-w-md max-h-[85vh] bg-white rounded-2xl flex flex-col shadow-xl overflow-hidden'
            )}
            dir="rtl"
          >
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-foreground font-persian">
                      موضوعات مورد علاقه
                    </h2>
                    <button
                      type="button"
                      onClick={handleTopicsCancel}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Modal Body - Scrollable */}
                <div className="overflow-y-auto p-6 max-h-[50vh]">
                  {topicsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : topicsError ? (
                    <ErrorDisplay
                      message={topicsError}
                      onRetry={refetchTopics}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {(Array.isArray(userTopics) ? userTopics : []).map((topic) => (
                        <SelectionCard
                          key={topic.topic_id}
                          title={topic.topic_name}
                          isSelected={tempSelectedTopicIds.includes(topic.topic_id)}
                          onClick={() => handleTopicToggle(topic.topic_id)}
                          className="h-full w-full"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Modal Footer - Fixed at bottom */}
                <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
                  {/* Success/Error Messages */}
                  {topicsSaveSuccess && (
                    <div className="mb-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center font-persian">
                      {topicsSaveSuccess}
                    </div>
                  )}
                  {topicsSaveError && (
                    <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center font-persian">
                      {topicsSaveError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleTopicsSave}
                    disabled={isSavingTopics}
                    className={
                      isSavingTopics
                        ? "w-full py-3 text-center font-persian rounded-lg transition-colors bg-gray-300 text-gray-500 cursor-not-allowed flex items-center justify-center gap-2"
                        : "w-full py-3 text-center font-persian rounded-lg transition-colors bg-primary-500 text-white hover:bg-primary-600"
                    }
                  >
                    {isSavingTopics && (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSavingTopics ? 'در حال ذخیره...' : 'ثبت'}
                  </button>
                </div>
          </div>
        </div>
        </div>
      )}

      <BottomNavigation />
      {/* Support Ticket Modal */}
      <SupportTicket isOpen={showSupportTicket} onClose={handleCloseSupportTicket} />
    </div >
  );
};

export default SettingsPage;
